import {
  BOARD,
  GROUP_INFO,
  MORTGAGE_INTEREST_PERCENT,
  OWNABLE_FIELDS,
  RAILROAD_FIELDS,
  STREET_FIELDS,
  UTILITY_FIELDS,
} from "@grundbesitz/shared";
import type { ColorGroup, Field, OwnableField, PlayerId, PlayerState, StateView, Trade, TradeSide } from "@grundbesitz/shared";

export function fieldAt(index: number): Field {
  return BOARD[index];
}

export function isOwnableField(f: Field): f is OwnableField {
  return f.kind === "STREET" || f.kind === "RAILROAD" || f.kind === "UTILITY";
}

export function ownableAt(index: number): OwnableField | null {
  const f = BOARD[index];
  return f && isOwnableField(f) ? f : null;
}

export function getPlayer(s: StateView, id: PlayerId): PlayerState | undefined {
  return s.players.find((p) => p.id === id);
}

export function activePlayers(s: StateView): PlayerState[] {
  return s.players.filter((p) => !p.eliminated);
}

export function currentPlayer(s: StateView): PlayerState {
  return s.players.find((p) => p.id === s.turn.playerId)!;
}

export function ownedFields(s: StateView, pid: PlayerId): number[] {
  return OWNABLE_FIELDS.filter((i) => s.properties[i].owner === pid);
}

export function groupFields(group: ColorGroup): number[] {
  return GROUP_INFO[group].fields;
}

export function groupOf(index: number): ColorGroup | null {
  const f = BOARD[index];
  return f.kind === "STREET" ? f.group : null;
}

/** Owner holds every street of the group (mortgaged or not). */
export function ownsGroup(s: StateView, pid: PlayerId, group: ColorGroup): boolean {
  return groupFields(group).every((i) => s.properties[i].owner === pid);
}

export function groupHasMortgage(s: StateView, group: ColorGroup): boolean {
  return groupFields(group).some((i) => s.properties[i].mortgaged);
}

export function groupHasBuildings(s: StateView, group: ColorGroup): boolean {
  return groupFields(group).some((i) => s.properties[i].buildings > 0);
}

/** True when the field belongs to a color group that carries at least one building. */
export function fieldGroupHasBuildings(s: StateView, index: number): boolean {
  const g = groupOf(index);
  return g !== null && groupHasBuildings(s, g);
}

/** Every street has an owner, none is left with the bank. */
export function allStreetsSold(s: StateView): boolean {
  return STREET_FIELDS.every((i) => s.properties[i].owner !== null);
}

export function mortgageValue(index: number): number {
  const f = ownableAt(index);
  return f ? f.mortgage : 0;
}

export function mortgageInterest(index: number): number {
  return Math.ceil((mortgageValue(index) * MORTGAGE_INTEREST_PERCENT) / 100);
}

/** Cost to lift a mortgage: value plus 10 %, rounded up to whole M. */
export function unmortgageCost(index: number): number {
  return mortgageValue(index) + mortgageInterest(index);
}

export function houseCostOf(index: number): number {
  const f = BOARD[index];
  return f.kind === "STREET" ? f.houseCost : 0;
}

export function countOwned(s: StateView, pid: PlayerId, fields: readonly number[]): number {
  return fields.filter((i) => s.properties[i].owner === pid).length;
}

/**
 * Rent for landing on `index`. Zero for unowned or mortgaged fields.
 * `diceTotal` is the sum of the landing player's current roll (utilities only).
 */
export function computeRent(s: StateView, index: number, diceTotal: number): number {
  const f = BOARD[index];
  const prop = s.properties[index];
  if (!prop || prop.owner === null || prop.mortgaged) return 0;
  const owner = prop.owner;
  switch (f.kind) {
    case "STREET": {
      if (prop.buildings > 0) return f.rents[prop.buildings];
      return ownsGroup(s, owner, f.group) ? f.rents[0] * 2 : f.rents[0];
    }
    case "RAILROAD": {
      const n = countOwned(s, owner, RAILROAD_FIELDS);
      return f.rents[n - 1];
    }
    case "UTILITY": {
      const n = countOwned(s, owner, UTILITY_FIELDS);
      return f.multipliers[n - 1] * diceTotal;
    }
    default:
      return 0;
  }
}

/** Houses and hotels a player owns. */
export function buildingCount(s: StateView, pid: PlayerId): { houses: number; hotels: number } {
  let houses = 0;
  let hotels = 0;
  for (const i of ownedFields(s, pid)) {
    const b = s.properties[i].buildings;
    if (b === 5) hotels++;
    else houses += b;
  }
  return { houses, hotels };
}

/** Half of the build price per building: what the bank pays when buildings are sold. */
export function buildingSaleValue(s: StateView, pid: PlayerId): number {
  let total = 0;
  for (const i of ownedFields(s, pid)) {
    total += (s.properties[i].buildings * houseCostOf(i)) / 2;
  }
  return total;
}

/**
 * Net worth for the time-limit mode: cash + purchase prices + building costs,
 * minus the mortgage value of mortgaged properties.
 */
export function netWorth(s: StateView, pid: PlayerId): number {
  const p = getPlayer(s, pid);
  if (!p || p.eliminated) return 0;
  let total = p.cash;
  for (const i of ownedFields(s, pid)) {
    const f = ownableAt(i)!;
    total += f.price;
    total += s.properties[i].buildings * houseCostOf(i);
    if (s.properties[i].mortgaged) total -= f.mortgage;
  }
  return total;
}

/** Maximum cash a player could gather by selling buildings and mortgaging everything. */
export function maxRaisable(s: StateView, pid: PlayerId): number {
  const p = getPlayer(s, pid);
  if (!p) return 0;
  let total = p.cash + buildingSaleValue(s, pid);
  for (const i of ownedFields(s, pid)) {
    if (!s.properties[i].mortgaged) total += mortgageValue(i);
  }
  return total;
}

export function debtsOf(s: StateView, pid: PlayerId) {
  return s.debts.filter((d) => d.debtor === pid);
}

export function debtTotal(s: StateView, pid: PlayerId): number {
  return debtsOf(s, pid).reduce((sum, d) => sum + d.amount, 0);
}

/** The player owes more than everything they could raise. */
export function isHopeless(s: StateView, pid: PlayerId): boolean {
  const owed = debtTotal(s, pid);
  return owed > 0 && maxRaisable(s, pid) < owed;
}

export interface BuildCheck {
  ok: boolean;
  reason?: string;
  cost: number;
  /** What would be placed: a house, or a hotel replacing four houses. */
  kind: "house" | "hotel";
}

/** Rule checks for building on `index` that do not depend on cash or the bank's supply. */
export function checkBuildRules(s: StateView, pid: PlayerId, index: number): BuildCheck {
  const f = BOARD[index];
  if (f.kind !== "STREET") return { ok: false, reason: "Nur auf Straßen kann gebaut werden.", cost: 0, kind: "house" };
  const prop = s.properties[index];
  const kind = prop.buildings === 4 ? "hotel" : "house";
  const fail = (reason: string): BuildCheck => ({ ok: false, reason, cost: f.houseCost, kind });
  if (prop.owner !== pid) return fail("Die Straße gehört dir nicht.");
  if (!ownsGroup(s, pid, f.group)) return fail("Dir gehört nicht die ganze Farbgruppe.");
  if (groupHasMortgage(s, f.group)) return fail("Eine Straße dieser Gruppe ist mit einer Hypothek belastet.");
  if (s.options.houseRules.buildAfterAllStreetsSold && !allStreetsSold(s)) {
    return fail("Gebaut werden darf erst, wenn alle Straßen verkauft sind.");
  }
  if (prop.buildings >= 5) return fail("Hier steht schon ein Hotel.");
  const min = Math.min(...groupFields(f.group).map((i) => s.properties[i].buildings));
  if (prop.buildings > min) return fail("Es muss gleichmäßig gebaut werden.");
  return { ok: true, cost: f.houseCost, kind };
}

/** Rule checks for selling a building on `index`. */
export function checkSellRules(s: StateView, pid: PlayerId, index: number): { ok: boolean; reason?: string } {
  const f = BOARD[index];
  if (f.kind !== "STREET") return { ok: false, reason: "Hier stehen keine Gebäude." };
  const prop = s.properties[index];
  if (prop.owner !== pid) return { ok: false, reason: "Die Straße gehört dir nicht." };
  if (prop.buildings === 0) return { ok: false, reason: "Hier steht kein Gebäude." };
  const max = Math.max(...groupFields(f.group).map((i) => s.properties[i].buildings));
  if (prop.buildings < max) return { ok: false, reason: "Es muss gleichmäßig zurückgebaut werden." };
  return { ok: true };
}

export function checkMortgageRules(s: StateView, pid: PlayerId, index: number): { ok: boolean; reason?: string } {
  const f = ownableAt(index);
  if (!f) return { ok: false, reason: "Dieses Feld kann nicht belastet werden." };
  const prop = s.properties[index];
  if (prop.owner !== pid) return { ok: false, reason: "Das Grundstück gehört dir nicht." };
  if (prop.mortgaged) return { ok: false, reason: "Das Grundstück ist bereits belastet." };
  if (f.kind === "STREET" && groupHasBuildings(s, f.group)) {
    return { ok: false, reason: "Vorher müssen alle Gebäude der Farbgruppe verkauft werden." };
  }
  return { ok: true };
}

/** Number of railroads/utilities/streets in bank hands. */
export function unownedFields(s: StateView): number[] {
  return OWNABLE_FIELDS.filter((i) => s.properties[i].owner === null);
}

/**
 * Counter offer to `t` from its recipient's view, asking `extra` more cash: the recipient first pays
 * less of its own cash, the rest is added to what the proposer pays.
 */
export function demandMore(t: Trade, extra: number): { give: TradeSide; get: TradeSide } {
  const net = t.give.cash - t.get.cash + extra;
  return {
    give: { cash: Math.max(0, -net), fields: [...t.get.fields], jailCards: [...t.get.jailCards] },
    get: { cash: Math.max(0, net), fields: [...t.give.fields], jailCards: [...t.give.jailCards] },
  };
}
